/**
 * Whether this browser is carrying a signed-in session.
 *
 * The whole session is the `bearerToken` cookie (`authProvider.ts`,
 * `auth-modal.tsx`, both login pages and `UploadPrefillPanel` all set it, and
 * `axios.ts` destroys it when the refresh fails). There is no client-side auth
 * context to ask, so the cookie IS the answer.
 *
 * Says nothing about WHO is signed in (`bearerSessionKey` only tells two
 * sessions apart), and is never a permission check. Every endpoint re-verifies the token server-side; this only decides
 * whether a control is worth putting in front of someone. A stale cookie makes
 * the answer optimistic, which costs one refused request and never grants
 * anything.
 *
 * The parsing is a pure function over a cookie string so it can be tested
 * without a document, the way `validate-coupon.ts` is.
 */

const COOKIE_NAME = "bearerToken";

/**
 * @param cookie the raw `document.cookie` string.
 *
 * Matched on the whole NAME, not a suffix: `adminBearerToken=` contains
 * `bearerToken=` and is a different cookie entirely. An empty value is signed
 * out — that is how a cookie is cleared on some paths, and offering credit on
 * the strength of an emptied token would produce a certain refusal.
 */
export function hasBearerCookie(cookie: string | null | undefined): boolean {
  if (!cookie) return false;
  return cookie.split(";").some((pair) => {
    const eq = pair.indexOf("=");
    if (eq === -1) return false;
    return pair.slice(0, eq).trim() === COOKIE_NAME && pair.slice(eq + 1).trim() !== "";
  });
}

/**
 * An opaque label for WHICH session this is, for keying per-account queries
 * (credit balances): without it, signing out and in as someone else within a
 * query's stale time showed the previous account's credit from the cache.
 *
 * A short FNV-1a hash of the cookie's value — never the token, never decoded,
 * so nothing secret ends up in the query cache or devtools. A refreshed token
 * gets a new label and costs one refetch. Null when signed out.
 */
export function bearerSessionKey(cookie: string | null | undefined): string | null {
  if (!cookie) return null;
  for (const pair of cookie.split(";")) {
    const eq = pair.indexOf("=");
    if (eq === -1 || pair.slice(0, eq).trim() !== COOKIE_NAME) continue;
    const value = pair.slice(eq + 1).trim();
    if (value === "") return null;
    let hash = 0x811c9dc5;
    for (let i = 0; i < value.length; i++) {
      hash ^= value.charCodeAt(i);
      hash = Math.imul(hash, 0x01000193);
    }
    return (hash >>> 0).toString(36);
  }
  return null;
}

/**
 * The signed-in account's email, read from the access token's `username`
 * claim WITHOUT verifying it. Only ever used to decide which stored credit
 * token may be sent (`credit-token.ts`); the server checks the token itself
 * on every request, so a forged cookie buys nothing. Null when signed out or
 * unreadable.
 */
export function bearerAccountEmail(cookie: string | null | undefined): string | null {
  if (!cookie) return null;
  for (const pair of cookie.split(";")) {
    const eq = pair.indexOf("=");
    if (eq === -1 || pair.slice(0, eq).trim() !== COOKIE_NAME) continue;
    try {
      const part = decodeURIComponent(pair.slice(eq + 1).trim()).split(".")[1];
      if (!part) return null;
      const base64 = part.replace(/-/g, "+").replace(/_/g, "/");
      const padded = base64 + "=".repeat((4 - (base64.length % 4)) % 4);
      const json = decodeURIComponent(
        Array.from(atob(padded), (c) => "%" + c.charCodeAt(0).toString(16).padStart(2, "0")).join(""),
      );
      const username = (JSON.parse(json) as { username?: unknown })?.username;
      return typeof username === "string" && username.trim()
        ? username.trim().toLowerCase()
        : null;
    } catch {
      return null;
    }
  }
  return null;
}

/** `bearerAccountEmail` of this browser's cookie; null on the server. */
export function accountEmail(): string | null {
  if (typeof document === "undefined") return null;
  return bearerAccountEmail(document.cookie);
}

/** `bearerSessionKey` of this browser's cookie; null on the server. */
export function sessionKey(): string | null {
  if (typeof document === "undefined") return null;
  return bearerSessionKey(document.cookie);
}

/** Server-safe: there is no `document` during SSR, and no session either. */
export function isSignedIn(): boolean {
  if (typeof document === "undefined") return false;
  return hasBearerCookie(document.cookie);
}

/**
 * Cookies fire no change event, so this is a best-effort subscription for
 * `useSyncExternalStore`: the snapshot is re-read on every render anyway (it is
 * a cheap string scan returning a boolean, so React sees a stable value and
 * does not loop), and these two events catch a sign-in that happened in another
 * tab or while this one was hidden. A sign-in in THIS tab — the checkout auth
 * modal — re-renders the tree by itself, and the fresh snapshot is read then.
 */
export function subscribeToSignIn(listener: () => void): () => void {
  if (typeof window === "undefined") return () => {};
  window.addEventListener("focus", listener);
  window.addEventListener("visibilitychange", listener);
  return () => {
    window.removeEventListener("focus", listener);
    window.removeEventListener("visibilitychange", listener);
  };
}
